import generateUUID from '../src/react/helpers/generateUUID';
import offlineProgressSync from '../src/react/helpers/OfflineProgressSync';
import { updateProgress } from '../src/react/helpers/progressHelper';
import { releaseFocusTrap } from './FocusTrap';
import { normalizeNodeUid } from './case_experience/normalize_node_uid';
import {
  documentUserStateResponseUnauthorized,
  rejectDocumentUserStateSaveResponse,
} from './case_experience/reject_document_user_state_save_response';
import {
  getCasePathContextFromDom,
  getNextVisibleDocSection,
} from './case_experience/case_path_visibility';
import { seemsOffline } from './connectivity';
import { isCaseOfflineEnabled } from './offline_gating';
import {
  clearLocalCaseProgress,
  getDocumentUserStateResetFromDom,
} from './clear_local_case_progress';

const AI_VPE_CHAT_MINIMUM_MESSAGE_COUNT = 5;

// Mirror of Documents::CaseExperience::StateChangePlanner::PASSIVE_ROOT_CARD_CLASSES (Ruby is authoritative).
// Enforced by spec/services/case_experience/passive_root_card_classes_sync_spec.rb
const PASSIVE_ROOT_CARD_CLASSES = [
  'feedback_card',
  'finish_case_card',
  'release_notes_card',
  'case_synopsis_card',
  'saq_card',
  'references_card',
  'learning_objectives_card',
];

const isPassiveRootCardSection = (section) => {
  if (!section?.classList) return false;
  return PASSIVE_ROOT_CARD_CLASSES.some((className) => section.classList.contains(className));
};

const filterAndFlattenActions = (actions) => {
  let filteredActions = actions.filter((action) =>
    Array.isArray(action) ? action.length > 0 : true,
  );
  filteredActions = filteredActions.flat();
  filteredActions = filteredActions.map((action) => {
    if (action.arguments?.uid) {
      action.arguments.uid = normalizeNodeUid(action.arguments.uid);
    }
    return action;
  });
  return filteredActions;
};

const getCsrfToken = () => {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
};

const documentUserStateEndpoint = (documentId, userId) => {
  return `/api/v2/document_user_states/${documentId}/${userId}`;
};

const updateDocumentUserState = async (payload) => {
  const body = payload.body || {
    document_user_state: {
      state: {
        [normalizeNodeUid(payload.nodeUid)]: {
          [payload.key]: {
            value: payload.value,
            updatedAt: new Date().getTime(),
          },
        },
      },
    },
  };
  body.document_user_state = {
    ...body.document_user_state,
    client_reset: getDocumentUserStateResetFromDom(),
  };

  if (shouldQueueDocumentUserState()) {
    const state = body.document_user_state?.state || {};
    await offlineProgressSync.queueDocumentUserStateUpdate(
      payload.documentId,
      payload.userId,
      state,
      'api/v2/document_user_states',
      body.document_user_state.client_reset,
    );
    const mergedState = mergeDocumentUserStateForContinueSync(getDusFromDom(), state);
    updateDusInDom(mergedState);
    syncAllContinueButtonsForDocument(mergedState);
    const envelope = { state: mergedState, progress: offlineCaseProgress(mergedState) };
    await offlineProgressSync.cacheDocumentUserState(payload.documentId, payload.userId, envelope);
    return envelope;
  }

  const response = await fetch(documentUserStateEndpoint(payload.documentId, payload.userId), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    await rejectDocumentUserStateSaveResponse(response);
  }

  const responseData = await response.json();

  const apiState = responseData.state && typeof responseData.state === 'object' ? responseData.state : {};
  const mergedState = mergeDocumentUserStateForContinueSync(getDusFromDom(), apiState);
  updateDusInDom(mergedState);
  syncAllContinueButtonsForDocument(mergedState);

  if (isCaseOfflineEnabled()) {
    await offlineProgressSync.cacheDocumentUserState(payload.documentId, payload.userId, {
      ...responseData,
      state: mergedState,
    });
  }

  return responseData;
};

const shouldQueueDocumentUserState = () => {
  return isCaseOfflineEnabled() && !navigator.onLine;
};

const getDocumentUserState = async (documentId, userId) => {
  if (isCaseOfflineEnabled() && !navigator.onLine) {
    return await offlineProgressSync.readCachedDocumentUserState(documentId, userId);
  }

  const response = await fetch(documentUserStateEndpoint(documentId, userId), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    },
  });

  if (!response.ok) {
    if (documentUserStateResponseUnauthorized(response)) return null;
    const errorData = await response
      .json()
      .catch(() => ({ errors: ['Failed to fetch document user state'] }));
    registerErrorInRollbar('Failed to fetch document user state', { detail: errorData });
    return null;
  }

  const responseData = await response.json();
  await offlineProgressSync.cacheDocumentUserState(documentId, userId, responseData);
  return responseData;
};

const executeActionsInDom = (actions, application) => {
  if (!application) throw new Error('Stimulus application instance is required');

  // Now update UI for each affected node
  actions.forEach(({ arguments: { uid, key, value } }) => {
    // Normalize the given uid, ensuring it matches the DOM convention
    const normUid = normalizeNodeUid(uid);

    // Skip updating the "root" node, which does not map to a UI element
    if (normUid === 'root') return;

    // Attempt to find the DOM node corresponding to this uid
    const node = document.querySelector(`[data-uid="${normUid}"]`);
    if (!node) return; // If the node isn't found, do nothing

    // Update section-collapse controller if present
    const sectionController = application.getControllerForElementAndIdentifier(
      node,
      'section-collapse',
    );
    if (sectionController) {
      sectionController.stateValue = { ...sectionController.stateValue, [key]: value };
      sectionController.updateUI();
    }

    // Update button-action controller if present
    const buttonController = application.getControllerForElementAndIdentifier(
      node,
      'button-action',
    );
    if (buttonController) {
      if (key === 'invisible') {
        const hidden = value === true || value === 'true';
        buttonController.invisibleValue = hidden;
        node.style.display = hidden ? 'none' : '';
      }
      if (key === 'inactive') {
        node.setAttribute('data-button-action-inactive-value', value);
      }
    }
  });
};

const normalizeActions = (actions) => {
  return actions.map((action) => {
    const nodeUid = normalizeNodeUid(action.arguments?.uid);
    if (action.action === 'setNodeInactive' && nodeUid) {
      return {
        action: 'setNodeKVPair',
        arguments: { uid: nodeUid, key: 'inactive', value: action.arguments.value },
      };
    } else if (action.action === 'setNodeInvisible' && nodeUid) {
      return {
        action: 'setNodeKVPair',
        arguments: {
          uid: nodeUid,
          key: 'invisible',
          value: action.arguments.value,
        },
      };
    }
    return action;
  });
};

const batchSetNodeKVPair = async (actions, documentId, userId) => {
  // Group by documentId/userId, then by nodeUid/key
  const state = {};

  actions = filterAndFlattenActions(actions);

  actions.forEach(({ arguments: { uid, key, value } }) => {
    if (!state[uid]) state[uid] = {};

    state[uid][key] = { value, updatedAt: Date.now() };
  });

  if (!documentId || !userId || Object.keys(state).length === 0) return;

  return await updateDocumentUserState({
    documentId,
    userId,
    body: { document_user_state: { state } },
  });
};

const submitFeedback = async (threadId, rating, issues, comment) => {
  const response = await fetch(`/ai_assistant/threads/${threadId}/feedbacks`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    },
    body: JSON.stringify({
      feedback: {
        smiley_rating: rating,
        issues,
        comment,
      },
    }),
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ errors: ['Failed to submit feedback'] }));
    registerErrorInRollbar('AI Assistant feedback submission failed', {
      threadId,
      rating,
      issues,
      comment,
      status: response.status,
      errorResponse: errorData,
    });
    throw new Error(errorData.errors?.[0] || 'Failed to submit feedback');
  }

  return response;
};

// Moves the current section to the next section in the DOM, updating collapsed/invisible state
const continueToNextSection = async ({
  element,
  documentId,
  userId,
  application,
  createContinueButtonIfNeeded = false,
}) => {
  if (!element) return;
  const currentSection = element.closest('.doc-section');
  if (!currentSection) return;

  const actions = [
    {
      uid: currentSection.dataset.uid,
      action: 'setNodeKVPair',
      arguments: { key: 'collapsed', uid: currentSection.dataset.uid, value: true },
    },
  ];

  const casePathContext = getCasePathContextFromDom();
  const nextSection = getNextVisibleDocSection(currentSection, casePathContext);

  if (nextSection?.classList.contains('doc-section')) {
    actions.push(
      {
        uid: generateUUID(),
        action: 'setNodeKVPair',
        arguments: { key: 'invisible', uid: nextSection.dataset.uid, value: false },
      },
      {
        uid: generateUUID(),
        action: 'setNodeKVPair',
        arguments: { key: 'collapsed', uid: nextSection.dataset.uid, value: false },
      },
    );
  }
  if (createContinueButtonIfNeeded) {
    actions.push(createNewContinueButtonInSection(nextSection, documentId, userId));
  }

  const updatedActions = filterAndFlattenActions(actions);
  const response = await batchSetNodeKVPair(updatedActions, documentId, userId);
  if (response && application) executeActionsInDom(updatedActions, application);

  return response;
};

const sectionHasContinueOrEquivalent = (section) => {
  if (!section) return false;
  if (section.querySelector('[data-case-viewer-target="continueButton"]')) return true;
  if (section.querySelector('[data-ai-assistant-feedback-target="continueButton"]')) return true;
  if (
    section.querySelector(
      '.doc-button-slice.doc-rails-button[data-controller="button-action"][data-button-action-name-value="continue"]',
    )
  ) {
    return true;
  }
  const slices = section.querySelectorAll('.doc-button-slice.doc-rails-button');
  for (const slice of slices) {
    const inner = slice.querySelector('.doc-button');
    if (inner && inner.textContent.trim().toUpperCase() === 'CONTINUE') return true;
  }
  return false;
};

const ensureSectionContinueHasCaseViewerTarget = (section) => {
  if (!section || section.querySelector('[data-case-viewer-target="continueButton"]')) return;
  const byName = section.querySelector(
    '.doc-button-slice.doc-rails-button[data-controller="button-action"][data-button-action-name-value="continue"]',
  );
  if (byName) {
    byName.setAttribute('data-case-viewer-target', 'continueButton');
    return;
  }
  const slices = section.querySelectorAll('.doc-button-slice.doc-rails-button');
  for (const slice of slices) {
    const inner = slice.querySelector('.doc-button');
    if (inner && inner.textContent.trim().toUpperCase() === 'CONTINUE') {
      slice.setAttribute('data-case-viewer-target', 'continueButton');
      return;
    }
  }
};

const PRE_CONTINUE_DIVIDER_HTML =
  '<hr class="aq-divider aq-divider--horizontal m-0 w-full case-experience-pre-continue-divider" aria-hidden="true">';

const createNewContinueButtonInSection = (section, documentId, userId) => {
  if (!section) return [];

  ensureSectionContinueHasCaseViewerTarget(section);

  const actions = [];
  const continueButtonId = `continue_button_${section.dataset.uid}`;
  if (!sectionHasContinueOrEquivalent(section)) {
    if (section.dataset?.aiDisplayFlag === 'aiOn' && section.dataset?.hasAiChildren === 'true') {
      return actions;
    }
    // Add button beforeend inside next section -> .doc-section-body > .doc-children element
    let inactive = 1;
    const docChildren = section?.querySelector('.doc-section-body .doc-children');
    if (docChildren) {
      const continueButtonActions = [];
      continueButtonActions.push({
        uid: generateUUID(),
        action: 'setNodeKVPair',
        arguments: { key: 'collapsed', uid: section.dataset.uid, value: true },
      });

      continueButtonActions.push({
        uid: generateUUID(),
        action: 'setNodeKVPair',
        arguments: { key: 'invisible', uid: continueButtonId, value: true },
      });

      continueButtonActions.push({
        uid: generateUUID(),
        action: 'setNodeKVPair',
        arguments: { key: 'inactive', uid: continueButtonId, value: 2 },
      });

      const nextSection = getNextSection(section);
      if (nextSection) {
        continueButtonActions.push({
          uid: generateUUID(),
          action: 'setNodeKVPair',
          arguments: { key: 'invisible', uid: nextSection.dataset.uid, value: false },
        });
        continueButtonActions.push({
          uid: generateUUID(),
          action: 'setNodeKVPair',
          arguments: { key: 'collapsed', uid: nextSection.dataset.uid, value: false },
        });
      }
      // Check if the section contains any button with the text `Submit`.
      const buttonsInSection = section.querySelectorAll('button');
      for (let i = 0; i < buttonsInSection.length; i++) {
        const btn = buttonsInSection[i];
        if (btn.textContent.trim().toLowerCase() === 'submit') {
          inactive = 2;
        }
      }

      const buttonHtml = `
        <div
          id="${continueButtonId}"
          data-is-node
          data-uid="${continueButtonId}"
          class="doc-button-slice doc-rails-button ${continueButtonId}"
          data-controller="button-action"
          data-case-viewer-target="continueButton"
          data-button-action-uid-value="${continueButtonId}"
          data-button-action-name-value="continue"
          data-button-action-document-id-value="${documentId}"
          data-button-action-user-id-value="${userId}"
          data-button-action-on-click-value='${JSON.stringify(continueButtonActions)}'
          data-button-action-invisible-value="false"
          data-button-action-inactive-value="${inactive}"
        >
          <button
            class="doc-button"
            data-action="click->button-action#handleClick"
          >
            CONTINUE
          </button>
        </div>
      `;
      docChildren.insertAdjacentHTML('beforeend', PRE_CONTINUE_DIVIDER_HTML + buttonHtml);
    }
  }

  return actions;
};

const getNextSection = (currentSection) => {
  return getNextVisibleDocSection(currentSection, getCasePathContextFromDom());
};

const currentSection = (element) => {
  return element.closest('.doc-section');
};

const waitForVisible = (el, cb) => {
  const isVisible = (elem) => {
    if (!elem) return false;
    const style = window.getComputedStyle(elem);
    return style.display !== 'none' && style.visibility !== 'hidden' && elem.offsetParent !== null;
  };
  if (isVisible(el)) {
    cb();
  } else {
    const observer = new MutationObserver(() => {
      if (isVisible(el)) {
        observer.disconnect();
        cb();
      }
    });
    observer.observe(document.body, { attributes: true, childList: true, subtree: true });
  }
};

// Restore the original on-click value for the continue button in the previous section
const restoreOriginalContinueButtonOnClick = (previousSection, actions) => {
  const updatedActions = [...actions];
  if (previousSection && previousSection.classList.contains('doc-section')) {
    const continueButtonContainer = previousSection.querySelector(
      '[data-case-viewer-target="continueButton"]',
    );
    if (continueButtonContainer) {
      const originalOnClick = continueButtonContainer.getAttribute(
        'data-original-button-action-on-click-value',
      );
      if (originalOnClick) {
        continueButtonContainer.setAttribute('data-button-action-on-click-value', originalOnClick);
      }
      updatedActions.push({
        uid: generateUUID(),
        action: 'setNodeKVPair',
        arguments: { key: 'invisible', uid: continueButtonContainer.dataset.uid, value: false },
      });
      updatedActions.push({
        uid: generateUUID(),
        action: 'setNodeKVPair',
        arguments: { key: 'inactive', uid: continueButtonContainer.dataset.uid, value: 1 },
      });
    }
  }

  return updatedActions;
};

// Show the AI error modal, with a callback for the continue button
const OFFLINE_AI_NOTICE_HEADING = 'AI features unavailable offline';
const OFFLINE_AI_NOTICE_BODY =
  'AI features are not available offline. If you proceed through this case while offline, you will need to reset the case when you have an internet connection in order to see or complete the AI features. You can complete the case offline, with non-AI versions of the features.';
const offlineAiNoticePanelHtml = (buttonId) => `
    <div class="gen-modal-panel gen-modal-panel-small ai-error-modal-panel">
      <h2>${OFFLINE_AI_NOTICE_HEADING}</h2>
      <p>${OFFLINE_AI_NOTICE_BODY}</p>
      <div class="ai-error-modal-actions">
        <button class="aq-button-2" id="${buttonId}">
          CONTINUE
        </button>
      </div>
    </div>
  `;

const showAiErrorModal = ({
  onContinue,
  buttonId = 'ai-assistant-error-modal-continue-button',
} = {}) => {
  document.querySelectorAll('.ai-error-modal').forEach((el) => el.remove());

  const modal = document.createElement('div');
  modal.className = 'ai-error-modal';
  modal.innerHTML = !seemsOffline()
    ? `
    <div class="gen-modal-panel gen-modal-panel-small ai-error-modal-panel">
      <h2>AN ERROR HAS OCCURRED</h2>
      <p>
        Oops! This feature is experiencing difficulty. Sorry for the hassle. You can still complete the case without the Virtual Patient Encounter. Your progress has been saved and you will be directed to the card immediately preceding the VPE. A record of this error has been logged.
      </p>
      <p>
        You can either proceed through the traditional format of this case without the VPE or return later when the error may be resolved.
      </p>
      <div class="ai-error-modal-actions">
        <button class="aq-button-2" id="${buttonId}">
          CONTINUE
        </button>
      </div>
    </div>
  `
    : offlineAiNoticePanelHtml(buttonId);

  document.body.appendChild(modal);
  document.body.style.overflow = 'hidden';

  const continueBtn = document.getElementById(buttonId);
  if (continueBtn && typeof onContinue === 'function') {
    continueBtn.addEventListener(
      'click',
      () => {
        continueBtn.disabled = true;
        onContinue();
      },
      { once: true },
    );
  }
};

// Show the AI VPE feedback error modal, with a callback for the continue button
const showAiVpeFeedbackErrorModal = ({
  onContinue,
  buttonId = 'ai-assistant-feedback-error-modal-continue-button',
} = {}) => {
  document.querySelectorAll('.ai-error-modal').forEach((el) => el.remove());

  const modal = document.createElement('div');
  modal.className = 'ai-error-modal';
  modal.innerHTML = !seemsOffline()
    ? `
    <div class="gen-modal-panel gen-modal-panel-small ai-error-modal-panel">
      <h2>AN ERROR HAS OCCURRED</h2>
      <p>
        Oops! This feature is experiencing difficulty. Sorry for the hassle. You can still complete the case without the Virtual Patient Feedback. Your progress has been saved and you will be directed to the card immediately preceding the VPE. A record of this error has been logged.
      </p>
      <p>
        You can either proceed through the traditional format of this case without the feedback or return later when the issue may be resolved.
      </p>
      <div class="ai-error-modal-actions">
        <button class="aq-button-2" id="${buttonId}">
          CONTINUE
        </button>
      </div>
    </div>
  `
    : offlineAiNoticePanelHtml(buttonId);

  document.body.appendChild(modal);
  document.body.style.overflow = 'hidden';

  const continueBtn = document.getElementById(buttonId);
  if (continueBtn && typeof onContinue === 'function') {
    continueBtn.addEventListener(
      'click',
      () => {
        continueBtn.disabled = true;
        onContinue();
      },
      { once: true },
    );
  }
};

// Shared fallback/restore logic for AI assistant error handling
// Hides the current section, traverses to the first previous section not aiOn, restores continue button, applies actions, and hides error modal
const handleAiAssistantFallback = async ({
  currentSectionElement,
  documentId,
  userId,
  application,
  batchSetNodeKVPairFn = batchSetNodeKVPair,
  restoreContinueButtonFn = restoreOriginalContinueButtonOnClick,
  executeActionsInDomFn = executeActionsInDom,
  onAfterFallback = null,
}) => {
  if (!currentSectionElement) return;

  const previousSiblingDocSection = (el) => {
    if (!el) return null;
    let p = el.previousElementSibling;
    while (p && !p.classList?.contains('doc-section')) {
      p = p.previousElementSibling;
    }
    return p;
  };

  // Update document container's data-case-viewer-ai-fallback-cards-value
  const documentContainer = document.querySelector('#document-container');
  if (documentContainer) {
    documentContainer.setAttribute('data-case-viewer-ai-fallback-cards-value', 'true');
  }

  // Update section-collapse controllers so aiOff sections become visible
  document.querySelectorAll('[data-section-collapse-ai-fallback-cards-value]').forEach((el) => {
    el.setAttribute('data-section-collapse-ai-fallback-cards-value', 'true');
  });

  const actions = [];

  actions.push({
    uid: generateUUID(),
    action: 'setNodeKVPair',
    arguments: { key: 'collapsed', uid: currentSectionElement.dataset.uid, value: true },
  });
  actions.push({
    uid: generateUUID(),
    action: 'setNodeKVPair',
    arguments: { key: 'invisible', uid: currentSectionElement.dataset.uid, value: true },
  });

  // Traverse back, collapsing previous sections whose data-ai-display-flag is 'aiOn'
  let previousSection = previousSiblingDocSection(currentSectionElement);
  while (previousSection && previousSection.getAttribute('data-ai-display-flag') === 'aiOn') {
    actions.push({
      uid: generateUUID(),
      action: 'setNodeKVPair',
      arguments: { key: 'collapsed', uid: previousSection.dataset.uid, value: true },
    });
    actions.push({
      uid: generateUUID(),
      action: 'setNodeKVPair',
      arguments: { key: 'invisible', uid: previousSection.dataset.uid, value: true },
    });
    previousSection = previousSiblingDocSection(previousSection);
  }

  // Find the "anchor" section: the first predecessor that is NOT aiOn
  // (this is the first section before the contiguous collapsed aiOn stack, moving toward the start of the case)
  const anchorAfterAiOnCollapse = previousSection;

  // Initialize a cursor at the anchor section to walk backwards and identify a contiguous run of aiOff sections
  let cursor = anchorAfterAiOnCollapse;
  let lastAiOffInRun = null;

  // Peel off the contiguous run of aiOff sections preceding the anchor section
  while (cursor && cursor.getAttribute('data-ai-display-flag') === 'aiOff') {
    lastAiOffInRun = cursor;
    cursor = previousSiblingDocSection(cursor);
  }

  /**
   * Utility to check if a given element:
   * - Exists
   * - Has the "doc-section" class
   * - Contains a Continue button (data-case-viewer-target="continueButton")
   * If so, returns the element; otherwise, returns null.
   */
  const pickSectionWithContinueButton = (el) => {
    if (!el || !el.classList?.contains('doc-section')) return null;
    return el.querySelector('[data-case-viewer-target="continueButton"]') ? el : null;
  };

  // Find the best section among: the first neutral boundary with Continue,
  // else the last aiOff in run with Continue, else the anchor (could be null)
  const restoreSectionForContinue =
    pickSectionWithContinueButton(cursor) ||
    pickSectionWithContinueButton(lastAiOffInRun) ||
    pickSectionWithContinueButton(anchorAfterAiOnCollapse);

  // Use restoreContinueButtonFn to update actions with any needed restore actions for the Continue button
  const updatedActions = restoreContinueButtonFn(restoreSectionForContinue, actions);

  // React case uses root.aiFallbackCards + client reflow (Container/showCardTest); tree visibility
  // updates without storing a landing section uid. Rails new_case_view is ERB + Stimulus: document
  // shape on screen does not reflow like React, so persist root.aiFallbackRevealContinueSectionUid
  // in DUS so helpers / case_viewer_controller know which section to expose for Continue after VPE error.
  const landingSectionUid = restoreSectionForContinue?.dataset?.uid;
  if (landingSectionUid) {
    updatedActions.push({
      uid: generateUUID(),
      action: 'setNodeKVPair',
      arguments: {
        uid: 'root',
        key: 'aiFallbackRevealContinueSectionUid',
        value: normalizeNodeUid(landingSectionUid),
      },
    });
  }

  const response = await batchSetNodeKVPairFn(updatedActions, documentId, userId);
  if (response) {
    executeActionsInDomFn(updatedActions, application);
    requestAnimationFrame(() => {
      // Hide the error modal after fallback actions
      document.querySelectorAll('.ai-error-modal').forEach((el) => el.remove());
      document.body.style.overflow = '';
      if (typeof onAfterFallback === 'function') onAfterFallback();
    });
  }
};

// Convert simple markdown to HTML (headings, bold, line breaks)
const convertSimpleMarkdown = (md) => {
  if (!md) return '';
  let html = md;
  // Headings: #, ##, ###, ####, #####
  html = html.replace(/^##### (.*)$/gm, '<h5>$1</h5>');
  html = html.replace(/^#### (.*)$/gm, '<h4>$1</h4>');
  html = html.replace(/^### (.*)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.*)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.*)$/gm, '<h1>$1</h1>');
  // Bold
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  // Italic
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  html = html.replace(/_(.*?)_/g, '<em>$1</em>');
  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  // Links [text](url)
  html = html.replace(
    /\[([^\]]+)\]\(([^\)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
  );
  // Unordered lists (lines starting with - or *)
  html = html.replace(/^(\s*)[-*] (.*)$/gm, '$1<li>$2</li>');
  // Wrap consecutive <li> in <ul>
  html = html.replace(/(<li>.*<\/li>\s*)+/g, (match) => `<ul>${match.replace(/\s*$/, '')}</ul>`);
  // Line breaks: remove newlines immediately after block tags, then convert remaining to <br>
  html = html.replace(/(<\/h[1-5]>|<\/ul>|<\/ol>)\s*\n/g, '$1');
  html = html.replace(/\n/g, '<br>');

  return html;
};

const getDusFromDom = () => {
  const documentContainer = document.getElementById('document-container');
  if (!documentContainer) return {};
  const dusData = documentContainer.getAttribute('data-case-viewer-document-user-state-value');
  if (!dusData) return {};
  try {
    return JSON.parse(dusData);
  } catch (e) {
    console.error('Failed to parse document user state JSON:', dusData, e);
    registerErrorInRollbar('Failed to parse document user state JSON', {
      rawValue: dusData,
      error: e,
    });
    return {};
  }
};

const offlineCaseProgress = (state) => {
  const uids = JSON.parse(
    document.getElementById('document-container').getAttribute('data-progress-marker-uids'),
  );
  const consumed = uids.filter((uid) => getDusEntryForUid(state, uid)?.invisible?.value === true).length;
  return Math.trunc((consumed / uids.length) * 100);
};

const vpeChatErrorBlocksFeedback = (dus) => {
  const root = dus?.root;
  if (!root || typeof root !== 'object') return false;
  if (root.error?.value === 'aiAssistantChatError') return true;
  if (root.aiFallbackCards?.value === true) return true;
  return false;
};

const QUESTION_ROOT_SELECTOR = [
  '.doc-multiple-choice-wrapper[data-controller~="multiple-choice"]',
  '.doc-rails-text-input[data-controller~="notes"]',
  '.doc-rails-ai-assistant-text-input[data-controller~="ai-assistant-text-input"]',
  '.doc-category-matcher[data-controller~="category-matcher"]',
  '.doc-rating-bar[data-controller~="rating-bar"]',
].join(', ');

const getDusEntryForUid = (state, uid) => {
  if (!state || uid == null) return null;
  const n = normalizeNodeUid(uid);
  if (state[n]) return state[n];
  const spaced = n.split('-').join(' ');
  if (state[spaced]) return state[spaced];
  const hyphenated = n.split(' ').join('-');
  if (state[hyphenated]) return state[hyphenated];
  if (n.startsWith('_ws_') && state['']) return state[''];
  return null;
};

const isQuestionKindSubmitted = (entry, kind) => {
  if (!entry) return false;
  if (kind === 'generic_ai_component') {
    const gradedDone = !!(entry.graded && entry.graded.value === true);
    if (!gradedDone) return false;
    if (entry.genericAiComponentResponseError?.value) return true;
    if (entry.genericAiComponentFeedbackSubmitted?.value === true) return true;
    if (!entry.threadId?.value) return true;
    return false;
  }
  if (kind === 'rating') {
    const v = entry.value;
    if (v && typeof v === 'object' && 'value' in v) {
      return Number(v.value) > 0;
    }
    return Number(v) > 0;
  }
  const gradedDone = !!(entry.graded && entry.graded.value === true);
  if (!gradedDone) return false;
  const aiFeedbackReceived =
    entry.aiSummaryStatementResponseRecieved?.value === true ||
    !!(entry.aiSummaryStatementFeedback?.value &&
      String(entry.aiSummaryStatementFeedback.value).trim().length > 0);
  if (aiFeedbackReceived) {
    return entry.aiSummaryStatementUserRatingSubmitted?.value === true;
  }
  return true;
};

const collectCardQuestionDescriptors = (cardSection) => {
  if (!cardSection) return [];
  const seen = new Set();
  const out = [];
  cardSection.querySelectorAll(QUESTION_ROOT_SELECTOR).forEach((el) => {
    const controllers = (el.getAttribute('data-controller') || '').split(/\s+/).filter(Boolean);
    let uid = null;
    let kind = 'graded';
    if (controllers.includes('multiple-choice')) {
      uid = el.getAttribute('data-multiple-choice-node-uid-value');
      if (uid && !el.querySelector('.doc-multiple-choice-answer, [data-multiple-choice-target="answer"]')) {
        return;
      }
    } else if (controllers.includes('ai-assistant-text-input')) {
      uid = el.getAttribute('data-ai-assistant-text-input-node-uid-value');
      kind = 'generic_ai_component';
    } else if (controllers.includes('notes')) {
      uid = el.getAttribute('data-notes-node-uid-value');
    } else if (controllers.includes('category-matcher')) {
      uid = el.getAttribute('data-category-matcher-node-uid-value');
    } else if (controllers.includes('rating-bar')) {
      uid = el.getAttribute('data-rating-bar-node-uid-value');
      kind = 'rating';
    }
    if (!uid) return;
    const key = `${normalizeNodeUid(uid)}:${kind}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ uid: normalizeNodeUid(uid), kind });
  });
  return out;
};

const mergeDocumentUserStateForContinueSync = (domState, apiState) => {
  const dom = domState && typeof domState === 'object' ? domState : {};
  const api = apiState && typeof apiState === 'object' ? apiState : {};
  const merged = { ...dom };
  for (const uid of Object.keys(api)) {
    merged[uid] = { ...(dom[uid] || {}), ...(api[uid] || {}) };
  }
  return merged;
};

const refreshContinueButtonController = (continueEl, application) => {
  if (!continueEl || !application) return;
  const buttonController = application.getControllerForElementAndIdentifier(
    continueEl,
    'button-action',
  );
  if (buttonController && typeof buttonController.updateButtonState === 'function') {
    buttonController.updateButtonState();
  }
};

const syncContinueButtonForCardSection = (section, application, state) => {
  if (!section) return;
  const cardSection = section.closest('[data-case-viewer-target="section"]') || section;
  const continueEl = cardSection.querySelector('[data-case-viewer-target="continueButton"]');
  if (!continueEl) return;

  const mergedState =
    state && typeof state === 'object' && Object.keys(state).length > 0 ? state : getDusFromDom();
  const descriptors = collectCardQuestionDescriptors(cardSection);
  if (descriptors.length === 0) return;

  const allSubmitted = descriptors.every(({ uid, kind }) =>
    isQuestionKindSubmitted(getDusEntryForUid(mergedState, uid), kind),
  );
  continueEl.setAttribute('data-button-action-inactive-value', allSubmitted ? '1' : '2');
  refreshContinueButtonController(continueEl, application);
};

const syncAllContinueButtonsForDocument = (state) => {
  const application = typeof window !== 'undefined' ? window.Stimulus : null;
  if (!application) return;

  const mergedState =
    state && typeof state === 'object' && Object.keys(state).length > 0 ? state : getDusFromDom();

  document.querySelectorAll('[data-case-viewer-target="continueButton"]').forEach((continueEl) => {
    const cardSection = continueEl.closest('[data-case-viewer-target="section"]');
    if (!cardSection) return;

    const descriptors = collectCardQuestionDescriptors(cardSection);
    if (descriptors.length === 0) return;

    const allSubmitted = descriptors.every(({ uid, kind }) =>
      isQuestionKindSubmitted(getDusEntryForUid(mergedState, uid), kind),
    );
    continueEl.setAttribute('data-button-action-inactive-value', allSubmitted ? '1' : '2');
    refreshContinueButtonController(continueEl, application);
  });
};

const updateDusInDom = (dus) => {
  const documentContainer = document.getElementById('document-container');
  if (!documentContainer) return;
  documentContainer.setAttribute('data-case-viewer-document-user-state-value', JSON.stringify(dus));
};

const isLegacyRailsSectionHidden = (section) => {
  if (!section) return true;
  if (section.style.display === 'none') return true;
  return section.classList.contains('hidden');
};

const lastVisibleCardHasAiAssistant = (section) => {
  if (!section) return false;
  return Boolean(
    section.querySelector(
      '[data-controller~="ai-assistant-chat"], [data-controller~="ai-assistant-feedback"]',
    ),
  );
};

const continueButtonHiddenInDom = (continueEl) => {
  if (!continueEl) return false;
  if (continueEl.style.display === 'none') return true;
  return continueEl.getAttribute('data-button-action-invisible-value') === 'true';
};

const shouldCatchUpRevealSection = (section) => {
  if (!section || !isLegacyRailsSectionHidden(section)) return false;
  if (section.classList.contains('finish_case_card')) return false;
  return !isPassiveRootCardSection(section);
};

const persistCatchUpActions = async (container, application, actions) => {
  const documentId = Number(container.dataset.caseViewerDocumentIdValue);
  const userId = Number(container.dataset.caseViewerUserIdValue);
  if (!documentId || !userId || actions.length === 0) return;

  try {
    const response = await batchSetNodeKVPair(actions, documentId, userId);
    if (response && application) executeActionsInDom(actions, application);
    return response;
  } catch {
    return null;
  }
};

const restoreContinueButton = (continueEl, application) => {
  if (!continueEl) return;
  continueEl.style.display = '';
  continueEl.setAttribute('data-button-action-invisible-value', 'false');
  const controller = application?.getControllerForElementAndIdentifier?.(continueEl, 'button-action');
  if (controller) controller.invisibleValue = false;
};

const lastVisibleNonPassiveCard = (sections) =>
  [...sections].reverse().find((section) => {
    if (isLegacyRailsSectionHidden(section)) return false;
    if (section.classList.contains('finish_case_card')) return false;
    return !isPassiveRootCardSection(section);
  });

const continueButtonInSection = (section) =>
  section?.querySelector('[data-case-viewer-target="continueButton"], [data-button-action-name-value="continue"]');

// One reload opens the first locked teaching card and shows its Continue.
// The next reload stops, because that Continue is visible.
const unstickLastVisibleContinueButton = async (
  application = typeof window !== 'undefined' ? window.Stimulus : null,
) => {
  const container = document.getElementById('document-container');
  if (!container) return;

  const sections = Array.from(container.querySelectorAll('[data-case-viewer-target="section"]'));
  const finishCaseCard = sections.find((section) => section.classList.contains('finish_case_card'));
  if (finishCaseCard && !isLegacyRailsSectionHidden(finishCaseCard)) return;

  const lastVisibleCard = lastVisibleNonPassiveCard(sections);
  if (!lastVisibleCard || lastVisibleCardHasAiAssistant(lastVisibleCard)) return;

  const continueEl = continueButtonInSection(lastVisibleCard);
  if (!continueButtonHiddenInDom(continueEl)) return;

  const nextSection = getNextSection(lastVisibleCard);
  if (shouldCatchUpRevealSection(nextSection)) {
    const uid = nextSection.dataset.uid;
    const nextContinue = continueButtonInSection(nextSection);
    const nextContinueUid = nextContinue?.id || nextContinue?.getAttribute('data-uid');
    const actions = [];
    if (uid) {
      actions.push(
        { action: 'setNodeKVPair', arguments: { uid, key: 'invisible', value: false } },
        { action: 'setNodeKVPair', arguments: { uid, key: 'collapsed', value: false } },
      );
    }
    if (nextContinueUid) {
      actions.push({
        action: 'setNodeKVPair',
        arguments: { uid: nextContinueUid, key: 'invisible', value: false },
      });
    }
    await persistCatchUpActions(container, application, actions);
    nextSection.style.display = '';
    nextSection.classList.remove('hidden');
    restoreContinueButton(nextContinue, application);
    return;
  }

  const continueUid = continueEl.id || continueEl.dataset.uid;
  if (continueUid) {
    await persistCatchUpActions(container, application, [
      { action: 'setNodeKVPair', arguments: { uid: continueUid, key: 'invisible', value: false } },
    ]);
  }
  restoreContinueButton(continueEl, application);
};

const filterActionIfAlreadyExistsInDus = (actions, dus) => {
  const existingDus = dus || {};
  actions = Array.isArray(actions) ? actions : [actions];

  return actions.filter((action) => {
    const dusForNode = existingDus[action.arguments.uid] || {};
    const existingValue = dusForNode[action.arguments.key]?.value;

    if (existingValue !== undefined && existingValue === action.arguments.value) {
      // Skip this action as it already exists in DUS with the same value
      return false;
    }

    return true;
  });
};

const registerErrorInRollbar = (message, detail = {}) => {
  if (
    typeof window !== 'undefined' &&
    window.Rollbar &&
    typeof window.Rollbar.error === 'function'
  ) {
    window.Rollbar.error(message, detail);
  }
};

const updateProgressFromResponse = (response) => {
  if (response && Number.isFinite(response.progress)) {
    updateProgress(response);
  }
};

// Extract answer_comment sibling HTML and remove from DOM if it's hidden (not yet revealed to the user). Returns { html, sibling } or null.
const extractHiddenAnswerCommentHTML = (element) => {
  let sibling = element.nextElementSibling;
  while (sibling) {
    if (
      sibling.classList &&
      sibling.classList.contains('doc-section') &&
      sibling.classList.contains('answer_comment') &&
      sibling.offsetParent === null // not visible in DOM
    ) {
      const html = sibling.outerHTML;
      sibling.remove();
      return { html, sibling };
    }
    sibling = sibling.nextElementSibling;
  }
  return null;
};

// Same walk as extractHiddenAnswerCommentHTML but removes the sibling regardless of visibility (e.g. .hidden overridden by doc display mode).
const extractAnswerCommentSiblingHtml = (element) => {
  let sibling = element.nextElementSibling;
  while (sibling) {
    if (
      sibling.classList &&
      sibling.classList.contains('doc-section') &&
      sibling.classList.contains('answer_comment')
    ) {
      const html = sibling.outerHTML;
      sibling.remove();
      return { html };
    }
    sibling = sibling.nextElementSibling;
  }
  return null;
};

const DOC_BODY_DISPLAY_MODES_REQUIRING_ANSWER_COMMENT_IN_DOM = [
  'full',
  'outline',
  'ungated',
];

const DISPLAY_MODES = ['student', ...DOC_BODY_DISPLAY_MODES_REQUIRING_ANSWER_COMMENT_IN_DOM];

const docBodyRequiresAnswerCommentInDom = (element) => {
  const docBody = element.closest('.doc-body');
  if (!docBody) return false;
  return DOC_BODY_DISPLAY_MODES_REQUIRING_ANSWER_COMMENT_IN_DOM.some((cls) =>
    docBody.classList.contains(cls),
  );
};

// Copy answer_comment sibling HTML without removing the node (needed when doc display mode CSS expects the section in the tree).
const snapshotAnswerCommentSiblingHtml = (element) => {
  let sibling = element.nextElementSibling;
  while (sibling) {
    if (
      sibling.classList &&
      sibling.classList.contains('doc-section') &&
      sibling.classList.contains('answer_comment')
    ) {
      return { html: sibling.outerHTML };
    }
    sibling = sibling.nextElementSibling;
  }
  return null;
};

// Appends answer_comment HTML after the given element's parent (section's children element) if not already present
const restoreAnswerCommentHTML = (element, answerCommentHtml) => {
  if (
    answerCommentHtml &&
    element.parentElement &&
    !element.parentElement.querySelector('.answer_comment')
  ) {
    element.parentElement.insertAdjacentHTML('beforeend', answerCommentHtml);
    const restored = element.parentElement.querySelector('.answer_comment');
    if (restored) {
      restored.classList.remove('hidden');
    }
  }
};

// Remove the answer comment from the UI (if present)
const removeAnswerCommentFromUI = (element) => {
  if (element.parentElement) {
    const answerComment = Array.from(element.parentElement.children).find(
      (el) => el.classList && el.classList.contains('answer_comment'),
    );
    if (answerComment) {
      answerComment.remove();
    }
  }
};

// Set display: 'block' for all elements with ids in the array
const setDisplayBlockByIds = (ids) => {
  if (!Array.isArray(ids)) return;
  ids.forEach(id => {
    const el = document.getElementById(id);
    if (el) {
      el.style.display = 'block';
    }
  });
};

// Set display: 'none' for all elements with ids in the array
const setDisplayNoneByIds = (ids) => {
  if (!Array.isArray(ids)) return;
  ids.forEach(id => {
    const el = document.getElementById(id);
    if (el) {
      el.style.display = 'none';
    }
  });
};

// Hide the modal and show the finish card content and section
const showFinishCardContentAndSection = () => {
  const completionModal = document.getElementById('case-completion-modal');
  if (completionModal) {
    releaseFocusTrap(completionModal);
  }
  setDisplayNoneByIds(['case-completion-modal']);
  setDisplayBlockByIds(['finish-card-content']);
  setDisplayBlockByIds(['finish-card-section']);
};

// Submits selected course IDs as student-choice cases for a document
const submitSelectedStudentChoiceCourses = async (documentId, courseIds) => {
  if (!documentId || !Array.isArray(courseIds) || courseIds.length === 0) return;

  const csrfToken = getCsrfToken();
  const response = await fetch(`/documents/${documentId}/add_as_student_choice_cases`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    },
    body: JSON.stringify({ courseIds }),
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { errors: ['Failed to add as student-choice case'] };
    }
    registerErrorInRollbar('Failed to add as student-choice case', { detail: errorData });
    throw new Error(errorData.errors?.[0] || 'Failed to add as student-choice case');
  }

  return response.json();
};

const noteSaveEpochsSettled = (detail) => {
  const start = detail?.saveStartEpoch;
  const end = detail?.saveEndEpoch;
  return start === undefined || start === end;
};

const shouldPreserveAnswerCommentInDom = () => {
  const el = document.getElementById('document-container');
  return el?.getAttribute('data-case-viewer-preserve-answer-comment-dom-value') === 'true';
};

/** True if stored HTML present or parent has another child that is `.doc-section.answer_comment` (any visibility). */
const removeSpeechToTextToolbarFromContainer = (container) => {
  if (!container?.querySelector) return;
  // Find the element containing the speech-to-text toolbar within the container.
  // Prefer `.doc-text-input-with-stt.speech-to-text` first (used in document text input sections),
  // otherwise fall back to `.ai-input-container.speech-to-text` (used in AI chat input).
  const wrap =
    container.querySelector('.doc-text-input-with-stt.speech-to-text') ||
    container.querySelector('.ai-input-container.speech-to-text');
  if (!wrap) return;
  const toolbar = wrap.querySelector('.speech-to-text__toolbar');
  if (toolbar?.isConnected) {
    toolbar.setAttribute('hidden', '');
    toolbar.style.display = 'none';
  }
  const status = wrap.querySelector('[data-speech-to-text-target="status"]');
  if (status?.isConnected) {
    status.setAttribute('hidden', '');
    status.style.display = 'none';
  }
};

const isSubmitButtonSlice = (element) => {
  if (!element?.classList?.contains('doc-button-slice')) return false;
  const name = (
    element.getAttribute('data-case-experience--button-action-name-value') ||
    element.getAttribute('data-button-action-name-value') ||
    ''
  )
    .trim()
    .toLowerCase();
  return name === 'submit';
};

const findSubmitButtonContainerAmongSiblings = (element) => {
  if (!element?.parentElement) return null;

  let passedElement = false;
  for (const child of element.parentElement.children) {
    if (child === element) {
      passedElement = true;
      continue;
    }
    if (passedElement && isSubmitButtonSlice(child)) {
      return child;
    }
  }

  let sib = element.nextElementSibling;
  while (sib) {
    if (isSubmitButtonSlice(sib)) return sib;
    sib = sib.nextElementSibling;
  }

  sib = element.previousElementSibling;
  while (sib) {
    if (isSubmitButtonSlice(sib)) return sib;
    sib = sib.previousElementSibling;
  }

  return null;
};

const findSubmitButtonContainerForElement = (element) => {
  if (!element) return null;

  const amongSiblings = findSubmitButtonContainerAmongSiblings(element);
  if (amongSiblings) return amongSiblings;

  const section = element.closest('.doc-section');
  if (!section) return null;

  const slices = section.querySelectorAll('.doc-button-slice');
  for (let i = 0; i < slices.length; i += 1) {
    const slice = slices[i];
    if (!isSubmitButtonSlice(slice)) continue;
    if (element.compareDocumentPosition(slice) & Node.DOCUMENT_POSITION_FOLLOWING) {
      return slice;
    }
  }

  for (let i = slices.length - 1; i >= 0; i -= 1) {
    const slice = slices[i];
    if (isSubmitButtonSlice(slice)) return slice;
  }

  return null;
};

const syncSubmitButtonInactiveState = (application, submitSlice, inactive) => {
  if (!submitSlice) return;

  submitSlice.setAttribute('data-button-action-inactive-value', String(inactive));
  const buttonAction = application?.getControllerForElementAndIdentifier(
    submitSlice,
    'button-action',
  );
  if (buttonAction) {
    buttonAction.inactiveValue = inactive;
    return;
  }

  const button = submitSlice.querySelector('button');
  if (!button) return;

  const disabled = inactive === 2;
  button.disabled = disabled;
  button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
};

const hasAnswerCommentAvailable = (element, storedAnswerCommentHtml) => {
  if (storedAnswerCommentHtml?.length) {
    return true;
  }
  let sibling = element.parentElement?.firstElementChild;
  while (sibling) {
    if (
      sibling !== element &&
      sibling.classList &&
      sibling.classList.contains('doc-section') &&
      sibling.classList.contains('answer_comment')
    ) {
      return true;
    }
    sibling = sibling.nextElementSibling;
  }
  return false;
};

const isFeedbackFinishSubmitButton = (buttonElement, nameValue) => {
  const name = (nameValue || '').toLowerCase().replace(/&amp;/g, '&');
  if (name.includes('finish_case') || name.includes('finish case')) return true;
  if (buttonElement?.closest?.('.doc-section.feedback_card')) return true;
  if (buttonElement?.closest?.('#feedback-modal-content')) return true;
  return false;
};

const actionsRevealFinishCaseCard = (actions) => {
  return (actions || []).some((action) => {
    const uid = normalizeNodeUid(action.arguments?.uid);
    return (
      uid === 'finish_case_card' &&
      action.arguments?.key === 'invisible' &&
      action.arguments?.value === false
    );
  });
};

const finishCaseCardWasHiddenInDus = (dus) => {
  const entry = getDusEntryForUid(dus, 'finish_case_card');
  return entry?.invisible?.value !== false;
};

const finishCaseCardVisibleInDus = (dus) => {
  const entry = getDusEntryForUid(dus, 'finish_case_card');
  return entry?.invisible?.value === false;
};

const shouldShowCompletionModals = ({
  actions,
  response,
  priorDus,
  buttonElement,
  nameValue,
}) => {
  if (isFeedbackFinishSubmitButton(buttonElement, nameValue)) return true;
  if (actionsRevealFinishCaseCard(actions)) return true;

  const apiState =
    response?.state && typeof response.state === 'object' ? response.state : {};
  if (finishCaseCardVisibleInDus(apiState) && finishCaseCardWasHiddenInDus(priorDus)) {
    return true;
  }

  const merged = mergeDocumentUserStateForContinueSync(priorDus, apiState);
  return finishCaseCardVisibleInDus(merged) && finishCaseCardWasHiddenInDus(priorDus);
};

const findFinishCaseCardSection = () => {
  return document.querySelector('.doc-section.finish_case_card');
};

const revealFinishCaseCardSection = (application) => {
  const section = findFinishCaseCardSection();
  if (!section) return;

  const sectionController = application?.getControllerForElementAndIdentifier?.(
    section,
    'section-collapse',
  );
  if (sectionController) {
    sectionController.stateValue = {
      ...sectionController.stateValue,
      invisible: false,
      collapsed: false,
    };
    sectionController.updateUI();
  } else if (section.style.display === 'none') {
    section.style.display = '';
  }
};

export {
  updateDocumentUserState,
  getDocumentUserState,
  getCsrfToken,
  clearLocalCaseProgress,
  getDocumentUserStateResetFromDom,
  executeActionsInDom,
  normalizeActions,
  batchSetNodeKVPair,
  submitFeedback,
  continueToNextSection,
  getNextVisibleDocSection,
  getCasePathContextFromDom,
  currentSection,
  getNextSection,
  waitForVisible,
  restoreOriginalContinueButtonOnClick,
  showAiErrorModal,
  showAiVpeFeedbackErrorModal,
  handleAiAssistantFallback,
  createNewContinueButtonInSection,
  isPassiveRootCardSection,
  PASSIVE_ROOT_CARD_CLASSES,
  sectionHasContinueOrEquivalent,
  AI_VPE_CHAT_MINIMUM_MESSAGE_COUNT,
  convertSimpleMarkdown,
  getDusFromDom,
  mergeDocumentUserStateForContinueSync,
  vpeChatErrorBlocksFeedback,
  syncContinueButtonForCardSection,
  syncAllContinueButtonsForDocument,
  filterActionIfAlreadyExistsInDus,
  unstickLastVisibleContinueButton,
  updateDusInDom,
  registerErrorInRollbar,
  updateProgressFromResponse,
  extractHiddenAnswerCommentHTML,
  hasAnswerCommentAvailable,
  noteSaveEpochsSettled,
  extractAnswerCommentSiblingHtml,
  docBodyRequiresAnswerCommentInDom,
  snapshotAnswerCommentSiblingHtml,
  restoreAnswerCommentHTML,
  removeSpeechToTextToolbarFromContainer,
  removeAnswerCommentFromUI,
  setDisplayBlockByIds,
  setDisplayNoneByIds,
  showFinishCardContentAndSection,
  submitSelectedStudentChoiceCourses,
  DISPLAY_MODES,
  normalizeNodeUid,
  getDusEntryForUid,
  shouldPreserveAnswerCommentInDom,
  isSubmitButtonSlice,
  findSubmitButtonContainerForElement,
  syncSubmitButtonInactiveState,
  isFeedbackFinishSubmitButton,
  actionsRevealFinishCaseCard,
  shouldShowCompletionModals,
  revealFinishCaseCardSection,
  findFinishCaseCardSection,
  seemsOffline,
  OFFLINE_AI_NOTICE_BODY,
};
